import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronsUpDown, Check, ClipboardList, Download, FileSpreadsheet, File, FileType, FileJson, FileText } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";

const API = process.env.REACT_APP_BACKEND_URL;

const mesesOpciones = [
  { value: 1, label: "Enero" }, { value: 2, label: "Febrero" }, { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" }, { value: 5, label: "Mayo" }, { value: 6, label: "Junio" },
  { value: 7, label: "Julio" }, { value: 8, label: "Agosto" }, { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" }, { value: 11, label: "Noviembre" }, { value: 12, label: "Diciembre" }
];

const mesShort = {
  1: "Ene", 2: "Feb", 3: "Mar", 4: "Abr", 5: "May", 6: "Jun",
  7: "Jul", 8: "Ago", 9: "Sep", 10: "Oct", 11: "Nov", 12: "Dic"
};

const RegistroVendedores = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [contribuyentes, setContribuyentes] = useState([]);
  const [rifPopoverOpen, setRifPopoverOpen] = useState(false);
  const [clasificadores, setClasificadores] = useState([]);
  const [filters, setFilters] = useState({ rif: "", nombre: "", categoria: "", ultimo_mes: "", tasa: "" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try { const r = await axios.get(`${API}/api/ambulantes/registro`, { headers }); setItems(r.data); }
    catch { toast.error("Error al cargar registros"); }
  };
  const fetchContribuyentes = async () => {
    try { const r = await axios.get(`${API}/api/contribuyentes/buscar?clase=sin-lae`, { headers }); setContribuyentes(r.data); }
    catch { setContribuyentes([]); }
  };
  const fetchClasificadores = async () => {
    try { const r = await axios.get(`${API}/api/ambulantes/clasificador`, { headers }); setClasificadores(r.data); }
    catch { setClasificadores([]); }
  };

  const handleSave = async () => {
    if (!formData.rif) { toast.error("RIF / Contribuyente es obligatorio"); return; }
    try {
      if (editingItem) {
        await axios.put(`${API}/api/ambulantes/registro/${editingItem.id}`, formData, { headers });
        toast.success("Registro actualizado exitosamente");
      } else {
        await axios.post(`${API}/api/ambulantes/registro`, formData, { headers });
        toast.success("Registro creado exitosamente");
      }
      setDialogOpen(false); setFormData({}); setEditingItem(null); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al guardar"); }
  };

  const openNew = () => {
    setEditingItem(null);
    setFormData({ clase_contribuyente: "Contribuyente sin LAE" });
    fetchContribuyentes(); fetchClasificadores();
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    fetchContribuyentes(); fetchClasificadores();
    setDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/api/ambulantes/registro/${itemToDelete.id}`, { headers });
      toast.success("Registro eliminado"); setDeleteDialogOpen(false); setItemToDelete(null); fetchData();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleContribuyenteSelect = (c) => {
    const rif = c.rif.includes("-") ? c.rif : `${c.tipo_rif || "V"}-${c.rif}`;
    const nombre = c.nombre || c.razon_social || c.denominacion_comercial || "";
    setFormData({ ...formData, rif, nombre_razon_social: nombre });
    setRifPopoverOpen(false);
  };

  const handleCategoriaChange = (categoriaName) => {
    const clasif = clasificadores.find(c => c.nombre === categoriaName);
    setFormData({
      ...formData,
      categoria: categoriaName,
      tasa: clasif ? clasif.tasa : 0
    });
  };

  const filtered = items.filter(item => {
    const f = filters;
    if (f.rif && !(item.rif || "").toLowerCase().includes(f.rif.toLowerCase())) return false;
    if (f.nombre && !(item.nombre_razon_social || "").toLowerCase().includes(f.nombre.toLowerCase())) return false;
    if (f.categoria && !(item.categoria || "").toLowerCase().includes(f.categoria.toLowerCase())) return false;
    if (f.tasa && !String(item.tasa || 0).includes(f.tasa)) return false;
    return true;
  });

  const formatUltimoMes = (item) => {
    if (!item.ultimo_mes || !item.ultimo_anio) return "-";
    return `${mesShort[item.ultimo_mes]} ${item.ultimo_anio}`;
  };

  // ============ EXPORT ============
  const getExportData = () => filtered.map(item => ({
    "RIF": item.rif || "",
    "Nombre / Razón Social": item.nombre_razon_social || "",
    "Último Mes Pago": formatUltimoMes(item),
    "Categoría": item.categoria || "",
    "Tasa": (item.tasa || 0).toFixed(2)
  }));

  const exportToExcel = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const ws = XLSX.utils.json_to_sheet(d); const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Registro Vendedores"); XLSX.writeFile(wb, `registro_vendedores_${new Date().toISOString().split("T")[0]}.xlsx`); toast.success("Exportado a Excel exitosamente"); };
  const exportToPDF = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const doc = new jsPDF("landscape"); doc.setFontSize(16); doc.text("Registro de Vendedores", 14, 20); doc.setFontSize(10); doc.text(`Fecha: ${new Date().toLocaleDateString("es-VE")}`, 14, 28); autoTable(doc, { head: [Object.keys(d[0])], body: d.map(r => Object.values(r)), startY: 35, styles: { fontSize: 8 }, headStyles: { fillColor: [234, 88, 12] } }); doc.save(`registro_vendedores_${new Date().toISOString().split("T")[0]}.pdf`); toast.success("Exportado a PDF exitosamente"); };
  const exportToCSV = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const h = Object.keys(d[0]).join(","); const rows = d.map(r => Object.values(r).map(v => `"${v}"`).join(",")).join("\n"); saveAs(new Blob(["\ufeff" + h + "\n" + rows], { type: "text/csv;charset=utf-8" }), `registro_vendedores_${new Date().toISOString().split("T")[0]}.csv`); toast.success("Exportado a CSV exitosamente"); };
  const exportToText = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const hs = Object.keys(d[0]); const cw = hs.map(h => Math.max(h.length, ...d.map(r => String(r[h]).length))); let t = "REGISTRO DE VENDEDORES\n"; t += `Fecha: ${new Date().toLocaleDateString("es-VE")}\n`; t += "=".repeat(Math.max(cw.reduce((a, b) => a + b + 3, 0), 50)) + "\n\n"; t += hs.map((h, i) => h.padEnd(cw[i])).join(" | ") + "\n"; t += cw.map(w => "-".repeat(w)).join("-+-") + "\n"; d.forEach(r => { t += hs.map((h, i) => String(r[h]).padEnd(cw[i])).join(" | ") + "\n"; }); saveAs(new Blob([t], { type: "text/plain;charset=utf-8" }), `registro_vendedores_${new Date().toISOString().split("T")[0]}.txt`); toast.success("Exportado a Texto exitosamente"); };
  const exportToHTML = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const hs = Object.keys(d[0]); const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Registro de Vendedores</title><style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#ea580c}table{border-collapse:collapse;width:100%;margin-top:20px}th,td{border:1px solid #ddd;padding:10px;text-align:left;font-size:12px}th{background-color:#ea580c;color:white}tr:nth-child(even){background-color:#f9fafb}tr:hover{background-color:#f3f4f6}.fecha{color:#6b7280;margin-bottom:20px}</style></head><body><h1>Registro de Vendedores</h1><p class="fecha">Fecha: ${new Date().toLocaleDateString("es-VE")}</p><table><thead><tr>${hs.map(h => `<th>${h}</th>`).join("")}</tr></thead><tbody>${d.map(r => `<tr>${Object.values(r).map(v => `<td>${v}</td>`).join("")}</tr>`).join("")}</tbody></table></body></html>`; saveAs(new Blob([html], { type: "text/html;charset=utf-8" }), `registro_vendedores_${new Date().toISOString().split("T")[0]}.html`); toast.success("Exportado a HTML exitosamente"); };
  const exportToJSON = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } saveAs(new Blob([JSON.stringify(d, null, 2)], { type: "application/json" }), `registro_vendedores_${new Date().toISOString().split("T")[0]}.json`); toast.success("Exportado a JSON exitosamente"); };

  return (
    <div className="space-y-6" data-testid="registro-vendedores-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Registro de Vendedores</h1>
          <p className="text-slate-500">Gestion de registro de vendedores ambulantes</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
            <span className="font-bold text-blue-800">{filtered.length}</span>
            <span className="text-blue-600">de</span>
            <span className="font-bold text-blue-800">{items.length}</span>
            <span className="text-blue-600">registros</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="btn-exportar-reg-vend"><Download className="w-4 h-4" /> Exportar</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToExcel} className="gap-2 cursor-pointer"><FileSpreadsheet className="w-4 h-4" /> Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" /> PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToCSV} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" /> CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToText} className="gap-2 cursor-pointer"><File className="w-4 h-4" /> Texto</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToHTML} className="gap-2 cursor-pointer"><FileType className="w-4 h-4" /> HTML</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToJSON} className="gap-2 cursor-pointer"><FileJson className="w-4 h-4" /> JSON</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={openNew} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-nuevo-registro-vend">
            <Plus className="w-4 h-4 mr-2" /> Nuevo
          </Button>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>RIF</TableHead>
              <TableHead>Nombre / Razon Social</TableHead>
              <TableHead>Ultimo Mes Pago</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead className="text-right">Tasa</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
            <TableRow className="bg-slate-50/50 border-b">
              <TableHead className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.rif} onChange={(e) => setFilters({...filters, rif: e.target.value})} data-testid="filter-regvend-rif" /></TableHead>
              <TableHead className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.nombre} onChange={(e) => setFilters({...filters, nombre: e.target.value})} data-testid="filter-regvend-nombre" /></TableHead>
              <TableHead className="px-3 py-1.5"></TableHead>
              <TableHead className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.categoria} onChange={(e) => setFilters({...filters, categoria: e.target.value})} data-testid="filter-regvend-categoria" /></TableHead>
              <TableHead className="px-3 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={filters.tasa} onChange={(e) => setFilters({...filters, tasa: e.target.value})} data-testid="filter-regvend-tasa" /></TableHead>
              <TableHead className="px-3 py-1.5"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No hay registros</TableCell></TableRow>
            ) : filtered.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-mono text-sm">{item.rif || "-"}</TableCell>
                <TableCell className="text-sm">{item.nombre_razon_social || "-"}</TableCell>
                <TableCell className="text-sm">{formatUltimoMes(item)}</TableCell>
                <TableCell>
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                    {item.categoria || "-"}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-right font-medium">{(item.tasa || 0).toFixed(2)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`btn-edit-regvend-${item.id}`}><Pencil className="w-4 h-4 text-blue-600" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }} data-testid={`btn-delete-regvend-${item.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-orange-600" />
              {editingItem ? "Editar Registro" : "Nuevo Registro de Vendedor"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Clase de Contribuyente</Label>
              <Input value="Contribuyente sin LAE" readOnly className="bg-slate-50" data-testid="form-clase-regvend" />
            </div>

            {/* RIF Selector */}
            <div className="space-y-2">
              <Label>RIF / Contribuyente *</Label>
              <Popover open={rifPopoverOpen} onOpenChange={setRifPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between h-auto min-h-[40px] text-left font-normal" data-testid="form-rif-regvend">
                    {formData.rif ? `${formData.rif} - ${formData.nombre_razon_social || ""}` : "Seleccione contribuyente"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[450px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar por RIF o nombre..." />
                    <CommandList>
                      <CommandEmpty>No se encontraron contribuyentes</CommandEmpty>
                      <CommandGroup>
                        {contribuyentes.map((c) => (
                          <CommandItem
                            key={c.id || c.rif}
                            value={`${c.display_name || c.rif}`}
                            onSelect={() => handleContribuyenteSelect(c)}
                          >
                            <Check className={`mr-2 h-4 w-4 ${formData.rif === c.rif ? "opacity-100" : "opacity-0"}`} />
                            <span className="text-sm">{c.display_name || c.rif}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Nombre o Razon Social</Label>
              <Input value={formData.nombre_razon_social || ""} readOnly className="bg-slate-50" data-testid="form-nombre-regvend" />
            </div>

            {/* Ultimo mes de pago */}
            <div className="bg-slate-50 rounded-lg p-4 border">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Último mes de pago</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mes</Label>
                  <Select value={String(formData.ultimo_mes || "")} onValueChange={(v) => setFormData({...formData, ultimo_mes: parseInt(v)})}>
                    <SelectTrigger data-testid="form-ultimo-mes-regvend"><SelectValue placeholder="Seleccione mes" /></SelectTrigger>
                    <SelectContent>{mesesOpciones.map((m) => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Año</Label>
                  <Input type="number" value={formData.ultimo_anio ?? ""} onChange={(e) => setFormData({...formData, ultimo_anio: parseInt(e.target.value) || null})} placeholder="Ej: 2025" data-testid="form-ultimo-anio-regvend" />
                </div>
              </div>
            </div>

            {/* Categoria / Clasificacion */}
            <div className="space-y-2">
              <Label>Categoría o Clasificación del Vendedor</Label>
              <Select value={formData.categoria || ""} onValueChange={handleCategoriaChange}>
                <SelectTrigger data-testid="form-categoria-regvend"><SelectValue placeholder="Seleccione categoria" /></SelectTrigger>
                <SelectContent>
                  {clasificadores.map((c) => (
                    <SelectItem key={c.id || c.nombre} value={c.nombre}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tasa (auto-filled) */}
            <div className="space-y-2">
              <Label>Tasa</Label>
              <Input type="number" step="0.01" value={formData.tasa ?? ""} readOnly className="bg-slate-50 text-right font-medium" data-testid="form-tasa-regvend" />
              {formData.categoria && (
                <p className="text-xs text-slate-500">Valor de tasa obtenido automaticamente del Clasificador Ambulantes</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-guardar-regvend">{editingItem ? "Actualizar" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Confirmar Eliminacion</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Esta seguro de eliminar este registro?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="btn-confirm-delete-regvend">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistroVendedores;
